// File: src/pages/VendorDirectoryPage.jsx
import React, { useRef, useState } from "react";
import VendorDirectoryTable from "@/components/Vendors/VendorDirectoryTable";
import VendorFormModal from "@/components/Vendors/VendorFormModal";
import VendorForecastAlerts from "@/components/Procurement/VendorForecastAlerts";
import VendorPaymentsPage from "@/components/Procurement/VendorPaymentsPage";
import VendorAnalyticsPanel from "@/components/Vendors/VendorAnalyticsPanel";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/axios";

const TABS = [
  { id: "directory", label: "📘 Directory" },
  { id: "intelligence", label: "🧠 Executive Intelligence" },
  { id: "alerts", label: "📡 Forecast Alerts" },
  { id: "payments", label: "💰 Payments" },
];

export default function VendorDirectoryPage() {
  const [showModal, setShowModal] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [activeTab, setActiveTab] = useState("directory");
  const [downloading, setDownloading] = useState(false);
  const tableRef = useRef();

  const openAddModal = () => {
    setEditVendor(null);
    setShowModal(true);
  };

  const openEditModal = (vendor) => {
    setEditVendor(vendor);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditVendor(null);
  };

  const refreshVendors = () => {
    tableRef.current?.loadVendors?.();
  };

  const handleExportVendorsPdf = async () => {
    setDownloading(true);

    try {
      const res = await api.get("/vendors/export-pdf", {
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const popup = window.open(url, "_blank");

      if (!popup) {
        const a = document.createElement("a");
        a.href = url;
        a.download = "vendors.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }

      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error("Failed to export vendors PDF:", err);
      alert("Could not export PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen futura-bg text-foreground relative">
      <div className="conic-glow" />

      <div className="max-w-7xl mx-auto p-8 space-y-8">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">🏢 Vendor Intelligence Hub</h1>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              Supplier directory, executive scorecards, pricing intelligence,
              delivery reliability, payment exposure, and risk signals in one place.
            </p>
          </div>

          <Button
            onClick={openAddModal}
            className="bg-accent text-accent-foreground shadow-glow hover:opacity-90"
            aria-label="Add Vendor"
          >
            ➕ Add Vendor
          </Button>
        </header>

        <div className="flex flex-wrap gap-2">
          {TABS.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "ghost"}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {activeTab === "directory" && (
          <section className="bg-glass rounded-2xl p-4">
            <VendorDirectoryTable ref={tableRef} onEditVendor={openEditModal} />
          </section>
        )}

        {activeTab === "intelligence" && (
          <section className="bg-glass rounded-2xl p-4">
            <VendorAnalyticsPanel />
          </section>
        )}

        {activeTab === "alerts" && (
          <section className="bg-glass rounded-2xl p-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold">
                  📡 AI-Powered Forecast Alerts
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Forecast risk, confidence, fill-rate, price accuracy, and delivery signals.
                </p>
              </div>

              <Button
                onClick={handleExportVendorsPdf}
                disabled={downloading}
                className="bg-green-600 hover:bg-green-700 disabled:opacity-60"
                aria-label="Export Vendor PDF"
              >
                {downloading ? "⏳ Exporting..." : "📄 Export PDF"}
              </Button>
            </div>

            <VendorForecastAlerts />
          </section>
        )}

        {activeTab === "payments" && (
          <section className="bg-glass rounded-2xl p-4">
            <h2 className="mb-2 text-xl font-semibold">
              💰 Financial Ledger & Payments
            </h2>
            <VendorPaymentsPage embedded />
          </section>
        )}
      </div>

      {showModal && (
        <VendorFormModal
          open={showModal}
          onClose={closeModal}
          onSuccess={refreshVendors}
          initialData={editVendor}
        />
      )}
    </div>
  );
}